/**
 * Frontend ErrorHandler - Intercepts API errors and displays user-friendly messages
 * Implements second layer of sensitive data validation (first layer is server-side)
 * Displays toast/modal with Error ID for support reference
 */

import { ErrorResponse, Sanitizer, ErrorIdGenerator } from '@akfdev-com/shared';

/**
 * Custom exception thrown after error is displayed to user
 * Allows calling code to catch and handle the fact that error was already shown
 */
export class ErrorDisplayed extends Error {
  constructor(public readonly errorId: string) {
    super(`Error displayed to user: ${errorId}`);
    this.name = 'ErrorDisplayed';
  }
}

/**
 * Interface for UIErrorDisplay implementation
 * Allows for flexible UI implementation (toast, modal, etc.)
 */
export interface IUIErrorDisplay {
  showToast(options: {
    message: string;
    errorId: string;
    severity?: string;
    actions?: Array<{ label: string; onClick: () => void }>;
  }): void;

  showModal(options: {
    title: string;
    message: string;
    footer: string;
    buttons: Array<{ label: string; onClick?: () => void }>;
  }): void;
}

/**
 * Frontend ErrorHandler - Intercepts API responses and displays errors
 * Validates error contract and sanitizes messages before display
 * Implements exponential backoff for retryable errors
 */
export class ErrorHandler {
  private static uiDisplay: IUIErrorDisplay | null = null;
  private static lastRequest:
    | { url: string; options: RequestInit }
    | null = null;
  private static retryCount = 0;
  private static readonly MAX_RETRIES = 3;
  private static readonly BACKOFF_BASE_MS = 100;

  /**
   * Set the UI display implementation
   */
  static setUIDisplay(display: IUIErrorDisplay): void {
    ErrorHandler.uiDisplay = display;
  }

  /**
   * Intercept HTTP response and handle errors
   * Returns the response if ok, otherwise displays error and throws
   */
  static async handleResponse(response: Response): Promise<Response> {
    if (!response.ok) {
      let errorData: ErrorResponse;

      try {
        errorData = (await response.json()) as ErrorResponse;
      } catch (parseError) {
        // If we can't parse error response, show generic error
        const errorId = this.generateFallbackErrorId();
        ErrorHandler.displayError({
          statusCode: response.status,
          errorId,
          userMessage:
            'An unexpected error occurred. Please try again or contact support.',
          timestamp: new Date().toISOString(),
          retryable: false,
        });
      }

      return ErrorHandler.displayError(errorData!);
    }

    // Reset retry state on success
    ErrorHandler.retryCount = 0;

    return response;
  }

  /**
   * Display error to user with toast/modal and throw ErrorDisplayed
   * Validates message for sensitive data as second layer of defense
   */
  static displayError(error: ErrorResponse): never {
    const sanitizedMessage = ErrorHandler.validateUserMessage(
      error.userMessage,
    );
    const errorId = error.errorId || ErrorHandler.generateFallbackErrorId();
    const retryable = error.retryable ?? false;

    if (!ErrorHandler.uiDisplay) {
      console.error('UIErrorDisplay not configured');
      throw new ErrorDisplayed(errorId);
    }

    if (retryable) {
      // Show retry button for retryable errors
      ErrorHandler.uiDisplay.showToast({
        message: sanitizedMessage,
        errorId,
        severity: error.severity || 'WARN',
        actions: [
          {
            label: 'Retry',
            onClick: () => {
              ErrorHandler.retryLastRequest();
            },
          },
        ],
      });
    } else {
      // Non-retryable API errors still use the non-blocking bottom-right flash
      // card. Error details remain visible through the Error ID without
      // interrupting the user's current workflow with a modal backdrop.
      ErrorHandler.uiDisplay.showToast({
        message: sanitizedMessage,
        errorId,
        severity: error.severity || 'ERROR',
      });
    }

    throw new ErrorDisplayed(errorId);
  }

  /**
   * Validate user message doesn't contain sensitive data
   * Regex checks for: stack traces, emails, ownerIds, error types
   */
  static validateUserMessage(msg: string): string {
    // Pattern checks for sensitive information
    const sensitivePatterns = [
      /at\s+\w+\s*\(/, // Stack trace "at function("
      /\/\w+\/\d+:/, // File path pattern
      /Error(?=\s|$)/i, // Error class names ending the word (TypeError, ValidationError, etc.)
      /\bundefined\b/i, // JavaScript undefined keyword
      /\bnull\b(?!\s*\+)/i, // JavaScript null keyword (but not in sentences)
      /Uncaught|unhandled/i, // Uncaught exception indicators
      /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/, // Credit card pattern
      /\b\d{3}-\d{2}-\d{4}\b/, // SSN pattern
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, // UUID pattern
    ];

    // Check for sensitive patterns
    for (const pattern of sensitivePatterns) {
      if (pattern.test(msg)) {
        console.warn('Sensitive data detected in user message:', msg);
        return 'An error occurred. Please try again or contact support.';
      }
    }

    // Check for PII using Sanitizer patterns (emails, etc.)
    const sanitized = Sanitizer.sanitize(msg) as string;

    // If sanitizer changed the message significantly, it contained PII
    // (changed due to redaction - more than just normal formatting)
    if (
      sanitized !== msg &&
      (msg.includes('@') ||
        msg.match(/\d{3}-\d{2}-\d{4}/) ||
        msg.match(/\d{16}/))
    ) {
      console.warn('PII detected in user message:', msg);
      return 'An error occurred. Please try again or contact support.';
    }

    return msg;
  }

  /**
   * Retry the last failed request with exponential backoff
   */
  static async retryLastRequest(): Promise<void> {
    if (
      !ErrorHandler.lastRequest ||
      ErrorHandler.retryCount >= ErrorHandler.MAX_RETRIES
    ) {
      console.error(
        'Cannot retry: no last request or max retries exceeded',
        ErrorHandler.retryCount,
      );
      return;
    }

    // Calculate exponential backoff: 100ms, 200ms, 400ms
    const delayMs =
      ErrorHandler.BACKOFF_BASE_MS * Math.pow(2, ErrorHandler.retryCount);
    ErrorHandler.retryCount++;

    console.info(`Retrying after ${delayMs}ms (attempt ${ErrorHandler.retryCount})`);

    await new Promise((resolve) => setTimeout(resolve, delayMs));

    try {
      const response = await fetch(
        ErrorHandler.lastRequest.url,
        ErrorHandler.lastRequest.options,
      );
      await ErrorHandler.handleResponse(response);
    } catch (error) {
      console.error('Retry failed:', error);
      // Let error bubble up to trigger another error display
    }
  }

  /**
   * Generate a fallback Error ID if response doesn't include one
   */
  private static generateFallbackErrorId(): string {
    return `ERR-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }
}

/**
 * Capture unhandled browser errors and report to backend
 * Generates deterministic ErrorIds and sends to /api/errors endpoint
 */
export class ErrorReporter {
  /**
   * URL schemes used by browser-extension content scripts. Password managers
   * (Dashlane, 1Password, etc.) inject scripts that hook
   * `navigator.credentials.create` for passkey/WebAuthn handling. When those
   * scripts throw (e.g. "navigator.credentials is undefined"), the uncaught
   * error can surface on our `window`, but it originates outside our app and
   * is not actionable — reporting it only pollutes `/api/errors` with noise.
   */
  private static readonly NON_APP_SOURCE_SCHEMES = [
    'moz-extension:',
    'chrome-extension:',
    'safari-extension:',
    'safari-web-extension:',
    'webkit-masked-url:',
  ];

  /**
   * Returns true when an error's source cannot be attributed to our own
   * application bundle — i.e. it comes from an injected browser-extension
   * content script, or from anonymous/eval'd code that has no real script URL
   * (Firefox reports these as "<anonymous code>", sometimes appended to the
   * page URL). Such errors are intentionally NOT reported to the backend.
   */
  private static isNonApplicationSource(source: string): boolean {
    if (!source || source === 'unknown') {
      return true;
    }

    const normalized = source.toLowerCase();

    // Anonymous / eval'd / injected inline code with no attributable script.
    if (
      normalized.includes('<anonymous') ||
      normalized.includes('%3canonymous')
    ) {
      return true;
    }

    return ErrorReporter.NON_APP_SOURCE_SCHEMES.some((scheme) =>
      normalized.startsWith(scheme),
    );
  }

  /**
   * Initialize error reporter (call once on app startup)
   * Registers window event listeners for uncaught errors and unhandled rejections
   */
  static initialize(): void {
    // Capture uncaught errors (JS errors, TypeErrors, etc.)
    window.addEventListener('error', (event: ErrorEvent) => {
      ErrorReporter.captureUncaughtException(event);
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      ErrorReporter.captureUnhandledRejection(event);
    });
  }

  /**
   * Capture uncaught JavaScript errors (TypeError, ReferenceError, SyntaxError, etc.)
   * Generates deterministic ErrorId using ErrorIdGenerator
   * Redacts sensitive data before sending to backend
   */
  static captureUncaughtException(event: ErrorEvent): void {
    // Extract error information
    const errorType = event.error?.constructor?.name || 'UncaughtError';
    const message = event.message || 'Unknown error';
    const filename = event.filename || 'unknown';
    const lineno = event.lineno || 0;
    const colno = event.colno || 0;

    // Skip errors that originate outside our application bundle (browser
    // extensions injecting scripts, or anonymous/eval'd code). These are not
    // actionable and would otherwise flood /api/errors on every page load.
    if (ErrorReporter.isNonApplicationSource(filename)) {
      return;
    }

    // Generate deterministic ErrorId using ErrorIdGenerator (P3: determinism, P4: uniqueness)
    const errorId = ErrorIdGenerator.generate({
      errorType,
      message,
      context: {
        filename,
        lineno,
        colno,
      },
    });

    // Build sanitized payload (redact sensitive data - P16: PII redaction)
    const payload = {
      errorId,
      type: 'BrowserError',
      message: Sanitizer.sanitize(message) as string,
      source: Sanitizer.sanitize(filename) as string,
      lineno,
      colno,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      stack: event.error?.stack
        ? (Sanitizer.sanitize(event.error.stack) as string)
        : undefined,
    };

    // Send to backend error collection endpoint with graceful fallback
    ErrorReporter.sendErrorReport(payload);
  }

  /**
   * Capture unhandled promise rejections
   * Generates deterministic ErrorId using ErrorIdGenerator
   * Handles both Error objects and non-Error rejections
   */
  static captureUnhandledRejection(event: PromiseRejectionEvent): void {
    const reason = event.reason;
    let message = String(reason);
    let stack: string | undefined;

    // Extract message from Error object if available
    if (reason instanceof Error) {
      message = reason.message || 'Unhandled promise rejection';
      stack = reason.stack;
    }

    // Generate deterministic ErrorId using ErrorIdGenerator (P3: determinism, P4: uniqueness)
    const errorId = ErrorIdGenerator.generate({
      errorType: 'UnhandledPromiseRejection',
      message,
      context: {},
    });

    // Build sanitized payload (redact sensitive data - P16: PII redaction)
    const payload = {
      errorId,
      type: 'PromiseRejection',
      reason: Sanitizer.sanitize(message) as string,
      timestamp: new Date().toISOString(),
      stack: stack
        ? (Sanitizer.sanitize(stack) as string)
        : undefined,
    };

    // Send to backend error collection endpoint with graceful fallback
    ErrorReporter.sendErrorReport(payload);
  }

  /**
   * Send error report to backend /api/errors endpoint
   * Gracefully handles fetch failures (logs to console as fallback)
   */
  private static sendErrorReport(payload: Record<string, unknown>): void {
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch((err) => {
      // Graceful fallback: log to console if POST fails
      // This ensures errors are captured even if backend is unreachable
      console.error(
        'Failed to report error to backend. Error payload:',
        payload,
        'Fetch error:',
        err,
      );
    });
  }
}
